<template>
	<v-container class="mt-sm mb-2xl px-sm">
		<v-breadcrumbs />

		<v-h3 tag="h1" class="mt-md">
			{{ pageInfo[0]?.name }}
		</v-h3>

		<div class="my-2 flex w-full bg-gray-100 py-lg lg:my-10">
			<v-input
				v-model="query"
				class="mx-md w-full rounded-full bg-white lg:mx-auto lg:w-3/4"
				:placeholder="`${t('outlet-search')}`"
				type="search"
				size="md"
				:clearable="query.length > 0"
			/>
		</div>

		<client-only>
			<v-tab-group
				class="mt-lg"
				:selected-index="selectedTab"
				@change="changeTab"
			>
				<div class="col-span-full">
					<v-tab-list
						class="mb-9 flex flex-row gap-x-lg overflow-x-auto border-b pl-0.5"
					>
						<v-tab v-for="outletGroup in outletGroups" :key="outletGroup.name">
							{{ outletGroup.title }}
						</v-tab>
					</v-tab-list>
				</div>

				<div class="flex flex-col pb-lg lg:grid lg:grid-cols-3">
					<div class="order-2 lg:order-1">
						<v-tab-panels>
							<v-tab-panel
								v-for="outletGroup in outletGroups"
								:key="outletGroup.name"
							>
								<div class="flex flex-row justify-between border-b pb-md">
									<p class="my-auto text-sm leading-6 text-gray-700">
										{{ filteredOutlets.length }} {{ t("outlet-count") }}
									</p>
								</div>

								<template v-for="outlet in filteredOutlets" :key="outlet.id">
									<v-page-outlets-outlet
										:outlet="outlet"
										:location-coords="geoSearchLocation"
										:show-distance="!!geoSearchLocation"
										@opened="onOutletOpen(outlet)"
										@closed="onOutletClose"
									/>
								</template>
							</v-tab-panel>
						</v-tab-panels>
					</div>

					<div class="order-1 col-span-2 mb-lg lg:order-2 lg:my-0 lg:ml-36">
						<v-page-outlets-outlet-map-new
							v-if="!isLoading"
							:location="mapLocation"
							:markers="markers"
							:filtered-markers="visibleMarkers"
							:placeholder-image-src="mapPlaceholderImage"
						/>
					</div>
				</div>
			</v-tab-group>
		</client-only>
	</v-container>
</template>

<script setup lang="ts">
import {watchDebounced} from "@vueuse/core";
import {uniqBy} from "es-toolkit";
import type {ISbComponentType, ISbStoryData} from "storyblok-js-client";
import type {Coordinates} from "@pkg/shared/types";

// OutletType, ^
import type {MapMarker} from "@pkg/shared/types/components/maps";

import {useStoryblokStoriesList} from "@pkg/shared/composables/use-storyblok-stories-list";
import calculateDistance from "@pkg/shared/utils/calculate-distance";

const {t} = useI18n();

const {t: tg} = useI18n({
	useScope: "global",
});

definePageMeta({
	alias: ["/kauplused/", "/myymalat/"],
});

const {
	public: {googleApi},
} = useRuntimeConfig();

const selectedTab = ref<number>(0);
const query = ref("");
const mapLocation = ref(",");
const isLoading = ref(true);

const {
	data: {value: pageInfo},
} = await useStoryblokStoriesList("outlets-info", {
	starts_with: `${tg("storyblok.outlets")}/`,
	is_startpage: true,
});

const mapPlaceholderImage =
	pageInfo[0]?.content?.map_placeholder_image?.filename;

// const outletGroupsData: OutletType[] = outletGroupsResponse.value?.datasource_entries || [];

// Locations for tabs. This separates outlets to the provinces of Finland.
// The first tab is for all outlets. The rest are for the provinces.
const outletGroups = computed(() => [
	{
		title: t("all"),
		name: "all",
	},
	// ...outletGroupsData
	//     .map((item) => ({
	//         title: item.name,
	//         name: item.value,
	//     })),
]);

// const allOutlets = ref<ISbStoryData<ISbComponentType<string> & { [index: string]: string }>[]>([]);

const {
	data: {value: allOutlets},
} = await useStoryblokStoriesList("outlets", {
	starts_with: `${tg("storyblok.outlets")}/`,
	excluding_slugs: `${tg("storyblok.outlets")}/`,
	is_startpage: false,
	per_page: 100,
});

type Outlet = (typeof allOutlets)[0];

// User location for sorting outlets.
const geoSearchLocation = ref<Coordinates | undefined>();
const geoSearchSortedOutlets = ref<Outlet[]>([]);

const filteredOutlets = computed(() => {
	if (query.value.length > 0) {
		const term = query.value.toLowerCase();
		const outlets = allOutlets.filter((x) =>
			x.name.toLowerCase().includes(term),
		);

		// If we didn't manage to sort outlets by geocoding, or any request hasn't come through yet, just return title matches.
		if (!geoSearchLocation.value) {
			return outlets;
		}

		// Default to showing only geo-matching outlets that are reasonably close to the queried place
		let nearbyOutlets = geoSearchSortedOutlets.value.filter((outlet) => {
			const outletCoords = (outlet.content.location || "0,0")
				.split(",")
				.map(Number);
			const distance = calculateDistance(
				outletCoords[0],
				outletCoords[1],
				geoSearchLocation.value!.latitude,
				geoSearchLocation.value!.longitude,
			);
			return distance < 40;
		});

		// If no name matches OR close-by outlets, show a few outlets that are the closest to the geo-matched location
		if (outlets.length === 0 && nearbyOutlets.length === 0) {
			nearbyOutlets = geoSearchSortedOutlets.value.slice(0, 3);
		}

		// Show title matches first, since those are snappier to load than geo-coded matches and *if* they contain matches they're likely what the customer wants.
		return uniqBy([...outlets, ...nearbyOutlets], (x) => x.id);
	}
	if (selectedTab.value !== 0) {
		const outletGroup = outletGroups.value[selectedTab.value];
		return allOutlets.filter(
			(x) => x.content.outlet_location === outletGroup?.name,
		);
	}
	isLoading.value = false;
	return allOutlets;
});

const markers = computed<MapMarker[]>(() =>
	allOutlets
		.filter((x) => x.content.location?.length > 0)
		.map((x) => ({
			coordinates: x.content.location,
			title: x.name,
			id: x.id,
			content: x.content,
		})),
);

// Markers that are visible on the map. This is a subset of all outlets that have a location set.
// This adds additional properties to the markers for the map marker which can be viewed in info window.
const visibleMarkers = computed<MapMarker[]>(() =>
	filteredOutlets.value
		?.filter((x) => x.content.location?.length > 0)
		.map((x) => ({
			coordinates: x.content.location,
			title: x.name,

			full_slug: x.full_slug,
			id: x.id,
			content: x.content,
		})),
);

/** Update the location from the disclosure. */
function onOutletOpen(
	outletData: ISbStoryData<
		ISbComponentType<string> & {[index: string]: string}
	>,
) {
	mapLocation.value = outletData.content?.location?.replace(" ", ",") || ",";
}
/** Resets location when disclosure is closed. */
function onOutletClose() {
	mapLocation.value = ",";
}

function changeTab(index: number) {
	selectedTab.value = index;
	onOutletClose();
	query.value = "";
}

function sortGeoSearchOutlets(location: Coordinates) {
	geoSearchSortedOutlets.value = allOutlets.sort((a, b) => {
		const coordsA = (a.content.location || "0,0").split(",").map(Number);
		const coordsB = (b.content.location || "0,0").split(",").map(Number);

		const aDistance = calculateDistance(
			coordsA[0],
			coordsA[1],
			location.latitude,
			location.longitude,
		);

		const bDistance = calculateDistance(
			coordsB[0],
			coordsB[1],
			location.latitude,
			location.longitude,
		);

		return aDistance - bDistance;
	});
}

type GeoCodeResult = {coordinates?: Coordinates; isAccurate: boolean};

const geoCodeSearch = async (
	countryCode: string,
	search: string,
): Promise<GeoCodeResult> => {
	const response = await fetch(
		`https://maps.googleapis.com/maps/api/geocode/json?address=${search}&components=country:${countryCode}&key=${googleApi}`,
	);
	const data = await response.json();

	const coords = data.results?.length
		? data.results[0].geometry.location
		: null;
	const coordinates = coords
		? {latitude: coords.lat, longitude: coords.lng}
		: undefined;

	// The API usually returns just the country itself as the match when no real results
	const isAccurate =
		data.results?.length && data.results[0].types[0] !== "country";

	return {coordinates, isAccurate};
};

watch(query, () => {
	if (query.value.length > 0) {
		selectedTab.value = 0;
	}
});

// Fetch geocoding data on query change
watchDebounced(
	query,
	async () => {
		if (query.value === "") {
			geoSearchLocation.value = undefined;

			return;
		}

		try {
			const countryCode = t("countryCode");

			const {coordinates, isAccurate} = await geoCodeSearch(
				countryCode,
				query.value,
			);

			let matchCoords = coordinates;
			let matchIsAccurate = isAccurate;

			// Google Maps API doesn't allow us to use multiple countries in one query so we need to fetch the data separately for Finland and Åland islands. This is because Åland is considered its own country by Google.
			// If no match in Finland, try Åland
			if (countryCode === "FI" && !isAccurate) {
				const {coordinates, isAccurate} = await geoCodeSearch(
					"AX",
					query.value,
				);
				matchIsAccurate = isAccurate;
				if (isAccurate) {
					matchCoords = coordinates;
				}
			}

			if (!matchIsAccurate) {
				throw new Error("No good geo-coding match");
			}

			geoSearchLocation.value = matchCoords;

			if (geoSearchLocation.value) {
				sortGeoSearchOutlets(geoSearchLocation.value);
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.warn(error);
			geoSearchLocation.value = undefined;
		}
	},
	{debounce: 500},
);

useSeoMeta({
	title: pageInfo[0]?.content?.meta?.title,
	description: pageInfo[0]?.content?.meta?.description ?? undefined,
});
</script>

<i18n lang="json">
{
	"et": {
		"outlet-search": "Leia lähim kauplus või ladu",
		"outlet-count": "asukohta",
		"outlets-near-you": "lähimat kauplust sinule",
		"all": "Kõik",
		"trailer-rental": "Käru rent",
		"warehouse-pickup": "Kättesaamine kohapeal",
		"countryCode": "EE"
	},
	"fi": {
		"outlet-search": "Hae myymälää tai varastoa",
		"outlet-count": "myymälää",
		"outlets-near-you": "lähintä myymälää sinulle",
		"all": "Kaikki",
		"trailer-rental": "Peräkärry vuokrattavissa",
		"warehouse-pickup": "Nouda tuotteet varastosta",
		"countryCode": "FI"
	}
}
</i18n>
